// ================= THE DECK BUILDER =================
//
// The page a player builds a 30-card deck on. It is NOT the game shell: it loads no ui.js, no
// ui-shell.js, no narrative modules, no engine AI. What it borrows is the card tile (js/card-tile.js,
// extracted in phase 0 precisely so this page could have it without the shell) and the rules
// (js/deck-rules.js), so the deck a player assembles here is checked by the same code that will
// check it at load time.
//
// ONE STATE OBJECT, ONE RENDER. Every mutation goes through setDraft() and re-renders the pool, the
// deck and the issue list from DRAFT. There is deliberately no incremental DOM updating: a builder
// whose copy badges are patched in place is a builder whose badges can disagree with its deck, and
// that class of bug is invisible until someone saves a deck that is not the one they built.
//
// The one concession to cost is TILE_CACHE. A tile's markup is a pure function of (cardId, artKey)
// -- it says nothing about how many copies are in the deck, which is what the row around it says --
// so caching it is not incremental state, it is memoisation of something that cannot go stale
// within a draft. Without it a full render calls compactCardHtml 58 times and renders 116
// silhouettes on every keystroke of the name field.
(function () {
  'use strict';

  const R = window.DeckRules;
  const FORMAT = 'custom';
  const SIZE = R.DECK_SIZE;
  const MAX_COPIES = R.FORMATS[FORMAT].maxCopies;
  const TIERS = ['Style', 'Family', 'Universal'];
  // THE TIER'S PLAYER-FACING WORD. `'Family'` stays the identifier -- in card data, in DeckRules,
  // in the validator, in every saved deck and share code -- and 'Foundation' is what a player reads,
  // because "Family" already meant two other things in this game (an Attack Type and a Defense
  // Type). See tierLabel in js/card-tile.js, which owns the mapping; this delegates rather than
  // keeping a second copy, and falls back to the identifier if the tile module is ever absent.
  function tierWord(tier) {
    return (window.CardTile && window.CardTile.tierLabel) ? window.CardTile.tierLabel(tier) : tier;
  }
  // (DeckRules now holds the same map for the refusals IT builds -- see TIER_WORD there for why it
  // owns a copy rather than calling into a UI module, and the mirror assertion that keeps the two
  // from drifting. This page reads both: headings through here, legality messages through the
  // validator, and they have to say the same word.)

  // The draft. `cards` is a FLAT ARRAY WITH REPEATS rather than a {id: count} map, because that is
  // the shape DECKS uses, the shape makeFighter takes, and the shape the share encoder will want --
  // three consumers that would each need converting back if this were a map.
  const DRAFT = { id: null, name: '', style: null, artKey: null, description: '', cards: [] };

  const FILTERS = { search: '', tier: 'all', range: 'all', speed: 'all', category: 'all' };

  // WHAT THIS PLAYER HAS BOUGHT, or null for "not supplied". js/deck-rules.js draws no distinction
  // between null and an empty set — a signed-out player, a file:// copy and a player who owns none
  // of the season all get the same pool, which is the base one — so the builder starts at null and
  // only ever narrows to a real set once the server has said so.
  //
  // THE BUILDER DOES NOT SELL. It reads this and builds from it, exactly as it reads the card pool;
  // buying happens on library.html and nowhere else. That split is the user's decision and it is
  // also the cleaner one — giving this page a second job with its own failure modes (a purchase
  // that races the ceiling, a balance that needs repainting mid-edit) is how a screen that works
  // becomes a screen that mostly works.
  let OWNED = null;

  // The whole /api/store answer, kept because the pool hint and the balance strip both read it.
  // Null until it arrives, and null forever on a build with no accounts.
  let STORE = null;

  let PENDING_STYLE = null;    // the Style waiting on the drop-cards confirm, or null
  let PENDING_DELETE = null;   // the saved deck id waiting on the list's delete confirm, or null
  let SHEET_OPEN = false;      // phone: is the deck panel showing as an overlay
  let STATUS = '';             // one line of feedback under the actions
  // The saved list's own outcome line. NOT #db-status: the list shows on the start gate too, where
  // render() returns before the status element is on screen to say anything -- a delete done from
  // there would report into a hidden builder.
  let SAVED_MSG = '';
  const TILE_CACHE = new Map();

  const SPEED_WORD = { 3: 'Fast', 2: 'Standard', 1: 'Committed' };
  const esc = window.CardTile.escapeHtml;
  const el = id => document.getElementById(id);
  const cardsTable = () => window.CARDS;
  const label = key => (window.STYLE_LABELS || {})[key] || key;

  // ---------------- derived reads ----------------

  function copiesOf(id) {
    let n = 0;
    for (let i = 0; i < DRAFT.cards.length; i++) if (DRAFT.cards[i] === id) n++;
    return n;
  }

  function issues() {
    if (!DRAFT.style) return [];
    return R.validate(DRAFT.cards, DRAFT.style, FORMAT, OWNED);
  }

  function isLegal() { return DRAFT.style != null && issues().length === 0; }

  // THE CAP, in one place. Both the + button's disabled state and addCard() read this, so a button
  // that looks available and a call that refuses cannot disagree.
  //
  // Three separate reasons a card cannot go in, and the caller wants to know WHICH: two copies
  // already, the deck is full, or the tier floors can no longer be reached. The last one is the
  // ratio rule read forwards -- with 12 Style and 8 Family required, a player who spends 20 slots on
  // Universal cards has already lost, and being told at card 30 means undoing a lot of work.
  // DeckRules owns that arithmetic so the builder and the validator cannot drift.
  function addBlock(id) {
    if (copiesOf(id) >= MAX_COPIES) return { ok: false, reason: 'Already at ' + MAX_COPIES + ' copies' };
    const card = cardsTable()[id];
    const tier = card && card.ownershipTier;
    return R.canAddTier(DRAFT.cards, tier, FORMAT);
  }

  function canAdd(id) { return addBlock(id).ok; }

  // Would this card be foreign to `style`? Style-tier cards must match the Style exactly; Family
  // cards must match that Style's Family. Universal cards are legal everywhere, which is what the
  // tier means, and they are the cards that SURVIVE a Style change.
  function isForeignTo(id, style) {
    const card = cardsTable()[id];
    if (!card) return false;
    if (card.ownershipTier === 'Style') return card.style !== style;
    if (card.ownershipTier === 'Family') return card.ownershipGroup !== window.FAMILY_OF_STYLE[style];
    return false;
  }

  // The cards a switch to `style` would drop, as [{id, name, copies}] sorted by name.
  function casualtiesOf(style) {
    const C = cardsTable();
    const seen = Object.create(null);
    const out = [];
    DRAFT.cards.forEach(id => {
      if (!isForeignTo(id, style) || seen[id]) return;
      seen[id] = true;
      out.push({ id: id, name: (C[id] && C[id].name) || id, copies: copiesOf(id) });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }

  // ---------------- mutations ----------------

  function setDraft(patch) {
    Object.assign(DRAFT, patch);
    render();
  }

  function addCard(id) {
    if (!canAdd(id)) return false;
    DRAFT.cards = DRAFT.cards.concat([id]);
    STATUS = '';
    render();
    return true;
  }

  function removeCard(id) {
    const i = DRAFT.cards.indexOf(id);
    if (i < 0) return false;
    DRAFT.cards = DRAFT.cards.slice(0, i).concat(DRAFT.cards.slice(i + 1));
    STATUS = '';
    render();
    return true;
  }

  // Ask for a Style change. Returns { changed, dropped } so a caller (and the test) can tell the
  // free case from the one that opened the confirm, rather than inferring it from the DOM.
  //
  // A change that costs nothing does not ask. The confirm exists to stop someone losing 22 cards by
  // brushing a select, and putting it in front of a change that drops nothing would teach people to
  // click through it.
  function requestStyle(style) {
    if (style === DRAFT.style) return { changed: false, dropped: [] };
    const dropped = casualtiesOf(style);
    if (!dropped.length) {
      applyStyle(style);
      return { changed: true, dropped: [] };
    }
    PENDING_STYLE = style;
    render();
    return { changed: false, dropped: dropped };
  }

  function confirmStyleChange() {
    const style = PENDING_STYLE;
    if (!style) return false;
    DRAFT.cards = DRAFT.cards.filter(id => !isForeignTo(id, style));
    PENDING_STYLE = null;
    applyStyle(style);
    return true;
  }

  function cancelStyleChange() {
    PENDING_STYLE = null;
    render();
  }

  function applyStyle(style) {
    DRAFT.style = style;
    if (!DRAFT.artKey || !artIsForStyle(DRAFT.artKey, style)) DRAFT.artKey = defaultArtFor(style);
    paintAccent(style);
    render();
  }

  function newDeck(style) {
    DRAFT.id = null;
    DRAFT.name = '';
    DRAFT.description = '';
    DRAFT.cards = [];
    DRAFT.artKey = null;
    PENDING_STYLE = null;
    PENDING_DELETE = null;
    SAVED_MSG = '';
    STATUS = '';
    applyStyle(style);
  }

  // A clone is a real starting point rather than a reference: the list is copied, and the name is
  // prefixed and TRUNCATED to the field's own limit. Several built-in names run past 28 characters
  // once "Copy of " is on the front, and a name the field cannot hold is a name that gets silently
  // cut somewhere less visible later.
  function cloneFrom(deckId) {
    const meta = window.DECK_META[deckId];
    const list = window.DECKS[deckId];
    if (!meta || !list) return false;
    DRAFT.id = null;
    DRAFT.name = ('Copy of ' + meta.name).slice(0, R.NAME_MAX);
    DRAFT.description = String(meta.archetype || '').slice(0, R.DESC_MAX);
    DRAFT.cards = list.slice();
    DRAFT.artKey = deckId;
    PENDING_STYLE = null;
    PENDING_DELETE = null;
    SAVED_MSG = '';
    STATUS = '';
    DRAFT.style = meta.style;
    paintAccent(meta.style);
    render();
    return true;
  }

  // ---------------- art ----------------
  //
  // Art comes from js/fighter-art.js, not from the deck list. It used to be
  // Object.keys(window.DECK_META), which worked only because the fifteen art basenames happened to
  // be the fifteen deck ids -- so there was nowhere to put art that no deck wears, and none could
  // exist for a Style with no deck yet. The registry decouples the two; the fifteen original keys
  // are unchanged, so every deck saved and every share code issued before it still resolves.
  function artTable() { return window.FIGHTER_ART || {}; }
  function artKeys() { return Object.keys(artTable()); }
  function artIsForStyle(key, style) {
    const a = artTable()[key];
    return !!a && a.style === style;
  }
  // A new deck opens on art of its own Style. Falls through to any art at all rather than to
  // nothing, so a Style with no art yet still gets a figure instead of a blank tile.
  function defaultArtFor(style) {
    return artKeys().find(k => artIsForStyle(k, style)) || artKeys()[0] || null;
  }
  function artLabel(key) {
    const a = artTable()[key];
    return (a && a.label) || key;
  }

  // The Style picker IS the colour picker -- there is no colour field, by decision. The accent has
  // to arrive at SELECTION time rather than at bout start, so the page a player is building on is
  // already the colour the fighter will be. Same three tokens ui-shell.js sets, so the shared
  // stylesheet lights up identically.
  function paintAccent(style) {
    const rgb = window.styleRgb ? window.styleRgb(style) : '63 143 255';
    const root = document.documentElement;
    root.style.setProperty('--p1', 'rgb(' + rgb + ')');
    root.style.setProperty('--p1-rgb', rgb);
    root.style.setProperty('--p1-glow', 'rgb(' + rgb + ' / 0.30)');
  }

  // ---------------- rendering ----------------

  // THE CACHE KEY IS THE WHOLE MECHANISM. A tile's markup depends on the card and on the fighter art
  // drawn in its two silhouettes, and on nothing else -- so (cardId, artKey) is the complete
  // identity of the thing being cached and a hit can never be stale.
  //
  // There is deliberately no cache-clearing anywhere. An earlier version cleared on every Style and
  // art change, which made the key redundant: either mechanism alone was sufficient, so a mutant
  // that broke one survived on the other, and neither was really under test. One mechanism, and it
  // is the one that cannot be got wrong at a call site -- a caller who forgets to clear gets stale
  // art, whereas a caller cannot forget to pass an artKey it does not have. Growth is bounded at
  // 194 cards x 15 art keys of small strings, and only for the keys actually visited.
  function tileHtml(id) {
    const key = id + '|' + DRAFT.artKey;
    if (!TILE_CACHE.has(key)) {
      // `artKey`, not the old `{ deckId: artKey }` trick: this page is choosing a FIGURE, and it
      // must keep resolving once art exists that no deck wears. See figureFor in js/card-tile.js.
      const body = { artKey: DRAFT.artKey };
      // NOT `readonly` ANY MORE, and js/card-tile.js had already written the rule this follows:
      // tiles here were focus-skipped and aria-disabled "because a tile in a grid had no action",
      // and "a row's action is 'open the details', which is a real thing to do, so it earns real
      // button semantics rather than being excused from them". That was written about the store's
      // rows; it applies here the moment the tile HAS an action, which it does now. The pool was 58
      // cards a player could add to a deck and could not read, and the sitewide audit's finding
      // against this page was exactly "disabled buttons presenting detailed content".
      //
      // The + and - stay SIBLINGS of the tile and never children of it -- the constraint
      // poolRowHtml documents and deck-builder-ui-test asserts. This makes the tile a real button
      // again, so a control nested inside it would be invalid HTML firing two handlers on one tap.
      TILE_CACHE.set(key, window.CardTile.compactCardHtml(id, {
        fighter: body, opponent: body,
        // The technique window's figure is also keyed on (card, artKey) -- the same two things the
        // cache key already carries -- so the art switching with the picker needs nothing new here.
        // If the window ever depends on a third thing, this key has to grow with it.
        moveArt: true
      }));
    }
    return TILE_CACHE.get(key);
  }

  // ---------------- the card detail sheet ----------------
  //
  // THE SAME SHEET library.html OPENS, FROM THE SAME RENDERER. Reported by the author against the
  // live build: "the deck builder and the library are very similar and it seems weird that they
  // don't behave the same when trying to see the card detail." They were similar in every way
  // except the one that matters most here -- the store shows a card in full before you spend
  // credits on it, and the builder showed a tile before you spend a deck slot on it.
  //
  // js/card-detail.js is what makes this cheap. Before it existed the expanded card lived inside
  // ui.js and this page could not have one without loading the game shell; the store hit that wall
  // first and the extraction is what came out of it. This is the second caller, and it is the
  // reason a shared renderer was worth extracting rather than a second copy worth writing.
  //
  // THE FIGURE IS THE ONE BEING BUILT. The store passes null -- a card in a collection belongs to
  // no deck, so the generic figure is the honest one -- but here the player has chosen a Style, and
  // the silhouettes are drawn on the body they are building. Same artKey the tiles already use.
  //
  // ADD IS THE SHEET'S ACTION, and it routes to the SAME addCard the row's + does. That is the rule
  // library-ui.js states for its Buy ("there is deliberately no second purchase path") applied to
  // the equivalent action here: a player who has just read a card should not have to find its row
  // again to act on what they read. When a copy cannot go in, the refusal carries the sentence the
  // row's disabled + already carries, rather than a control that silently does nothing.
  function openCardSheet(id) {
    const card = cardsTable()[id];
    if (!card || !window.UISheet) return false;
    const body = { artKey: DRAFT.artKey };
    const art = window.CardTile.figureFor(body);
    const n = copiesOf(id);
    const block = addBlock(id);
    const actions = [];
    if (block.ok) {
      actions.push({
        label: 'Add a copy', kind: 'primary',
        onClick: function () { addCard(id); }
      });
    } else {
      // Present but inert, and SAYING WHY, which is the shape the row's + already has: "a disabled
      // control with no stated reason reads as a bug".
      actions.push({ label: block.reason, kind: 'ghost', onClick: function () {} });
    }
    actions.push({ label: 'Close', kind: 'ghost' });
    // Guarded exactly as library.html guards the same block, and degrading to the same thing: with
    // card-detail.js absent the sheet still opens and still shows the card, as the compact tile.
    const face = window.CardDetail
      ? '<div class="card-sheet-detail">' +
        window.CardTile.techniqueWindowHtml(card, { fighter: body, opponent: body }) +
        window.CardDetail.railDetailHtml(id, art) +
        '</div>'
      : '<div class="lib-sheet-card">' +
        window.CardTile.compactCardHtml(id, { readonly: true, moveArt: true, rules: true }) +
        '</div>';
    window.UISheet.open({
      title: card.name,
      bodyHtml: face +
        (window.MechanicsDictionary ? window.MechanicsDictionary.cardMechanicsHtml(card) : '') +
        '<p class="db-sheet-copies">' + esc(tierWord(card.ownershipTier)) + ' &middot; ' +
        n + ' of ' + MAX_COPIES + ' in this deck</p>',
      actions: actions
    });
    return true;
  }

  function matchesFilters(id) {
    const card = cardsTable()[id];
    if (FILTERS.tier !== 'all' && card.ownershipTier !== FILTERS.tier) return false;
    if (FILTERS.speed !== 'all' && String(window.ENGINE.printedSpeedOf(card)) !== FILTERS.speed) return false;
    if (FILTERS.category !== 'all') {
      const cat = (card.attack && card.attack.category) || '';
      if (FILTERS.category === 'none' ? !!cat : cat !== FILTERS.category) return false;
    }
    if (FILTERS.range !== 'all') {
      const r = card.attack && card.attack.optimalRanges;
      const hit = r === 'All' || (Array.isArray(r) && r.indexOf(FILTERS.range) >= 0);
      if (!hit) return false;
    }
    if (FILTERS.search) {
      // THE SAME SEARCH THE LIBRARY'S CARDS TAB RUNS, from the same function -- see
      // MechanicsDictionary.searchText. This half and that one carried identical haystacks of
      // name + id + moveGloss, which is flavour rather than rules: the printed text was
      // unsearchable in both of the two places a player goes looking for a card by what it DOES.
      // Two lists searching differently would be the same bug twice, so neither owns the answer.
      //
      // Guarded like every other MechanicsDictionary call here: with the module absent the pool
      // falls back to the search this page used to have, rather than to no pool at all.
      const M = window.MechanicsDictionary;
      if (M && M.matchesSearch) {
        if (!M.matchesSearch(card, FILTERS.search)) return false;
      } else {
        const q = FILTERS.search.toLowerCase();
        const hay = (card.name + ' ' + id + ' ' + (card.moveGloss || '')).toLowerCase();
        if (hay.indexOf(q) < 0) return false;
      }
    }
    return true;
  }

  // A pool row is a TILE PLUS CONTROLS SIDE BY SIDE, and the controls are never inside the tile.
  // compactCardHtml returns a <button>; a + button nested inside it would be invalid HTML, would be
  // unreachable by keyboard in some browsers, and would fire both handlers on one click.
  function poolRowHtml(id) {
    const n = copiesOf(id);
    const block = addBlock(id);
    const addDisabled = !block.ok;
    // A disabled control with no stated reason reads as a bug. Every refusal carries its own
    // sentence onto the button's title and aria-label, so the tier floor explains itself at the
    // moment it bites rather than only in the issue list.
    const why = block.ok ? 'Add a copy' : block.reason;
    // A FULL DECK IS THE ONE REFUSAL WITH A REMEDY, and on a phone the remedy is thirty rows away
    // behind a control the player has to know exists. So at 30/30 the + stays pressable and does
    // something else: it opens the deck panel with a line naming the card that is waiting. The
    // audit's "replace mode", scoped to what is honest -- nothing here invents a swap flow.
    //
    // Only when a copy could actually go in. A card already at the copy cap cannot be added by
    // freeing a slot, so offering to open the deck for it would be a promise the panel cannot keep;
    // that one stays disabled with its own sentence. And only where the panel is an overlay: on a
    // desktop it is a column already on screen, and a button that looks available and does not add
    // is worse than a disabled one standing next to the answer.
    const fullRemedy = !block.ok && DRAFT.cards.length >= SIZE &&
                       n < MAX_COPIES && sheetIsOverlay();
    const addLabel = fullRemedy
      ? 'Deck is full — open your deck to make room for ' + cardsTable()[id].name
      : (addDisabled ? why : 'Add ' + cardsTable()[id].name);
    return '<div class="db-row' + (n ? ' is-in-deck' : '') + '" data-card-id="' + esc(id) + '">' +
      '<div class="db-row-tile">' + tileHtml(id) + '</div>' +
      '<div class="db-row-ctl">' +
        '<button type="button" class="db-btn db-add' + (fullRemedy ? ' is-full-remedy' : '') +
          '" data-act="' + (fullRemedy ? 'add-full' : 'add') + '" data-card-id="' + esc(id) + '"' +
          (addDisabled && !fullRemedy ? ' disabled' : '') +
          ' title="' + esc(fullRemedy ? addLabel : why) + '" aria-label="' +
          esc(addLabel) + '">+</button>' +
        '<span class="db-copies' + (n >= MAX_COPIES ? ' is-max' : '') + '">' + n + '/' + MAX_COPIES + '</span>' +
        '<button type="button" class="db-btn db-remove" data-act="remove" data-card-id="' + esc(id) + '"' +
          (n ? '' : ' disabled') + ' title="Remove a copy" aria-label="Remove ' +
          esc(cardsTable()[id].name) + '">&minus;</button>' +
      '</div></div>';
  }

  function renderPool() {
    const pool = R.legalPool(DRAFT.style, OWNED);
    let total = 0, shown = 0, html = '';
    TIERS.forEach(tier => {
      const all = pool[tier];
      total += all.length;
      const list = all.filter(matchesFilters);
      shown += list.length;
      if (!list.length) return;
      html += '<div class="db-group"><h3 class="db-group-head">' + tierWord(tier).toUpperCase() +
        ' <span class="db-group-n">' + list.length +
        (list.length === all.length ? '' : ' of ' + all.length) + '</span></h3>' +
        '<div class="db-rows">' + list.map(poolRowHtml).join('') + '</div></div>';
    });
    if (!shown) {
      html = '<p class="db-empty">No card in this Style matches those filters. ' +
        '<button type="button" class="db-link" data-act="clear-filters">Clear filters</button></p>';
    }
    el('db-pool-list').innerHTML = html;
    el('db-pool-count').textContent = shown === total ? String(total) : shown + ' of ' + total;
  }

  function renderDeck() {
    const C = cardsTable();
    const grouped = { Style: [], Family: [], Universal: [] };
    const seen = Object.create(null);
    DRAFT.cards.forEach(id => {
      if (seen[id]) return;
      seen[id] = true;
      const card = C[id];
      const tier = (card && TIERS.indexOf(card.ownershipTier) >= 0) ? card.ownershipTier : null;
      if (tier) grouped[tier].push(id);
    });
    let html = '';
    TIERS.forEach(tier => {
      const list = grouped[tier].sort((a, b) => C[a].name.localeCompare(C[b].name));
      if (!list.length) return;
      html += '<div class="db-group"><h3 class="db-group-head">' + tierWord(tier).toUpperCase() + '</h3>' +
        '<div class="db-rows">' + list.map(id => {
          const n = copiesOf(id);
          return '<div class="db-row" data-card-id="' + esc(id) + '">' +
            '<div class="db-row-tile">' + tileHtml(id) + '</div>' +
            '<div class="db-row-ctl">' +
              (n > 1 ? '<span class="db-xn">&times;' + n + '</span>' : '') +
              '<button type="button" class="db-btn db-remove" data-act="remove" data-card-id="' +
                esc(id) + '" title="Remove a copy" aria-label="Remove ' + esc(C[id].name) +
                '">&minus;</button>' +
            '</div></div>';
        }).join('') + '</div></div>';
    });
    // ---- the two kinds of card this builder has no column for ----
    //
    // Both are invisible occupants of a deck slot if nothing draws them: the card counts toward 30,
    // validate() reports it, and the player has no control to remove it. So each gets a row and a
    // minus button, and they are separated because the two situations are not the same problem.
    //
    // A GHOST is an id with no card behind it -- a share code or a stored deck from a build that
    // had one. NOT BUILDABLE is a card that exists and resolves perfectly well in a bout but whose
    // ownership tier is not one this builder offers, which today means the Challenge tier: a
    // seasonal ability that reached a draft by cloning a deck that legally contains it. The rule
    // is written against the TIER LIST rather than against `Challenge` by name, so a tier added
    // later and forgotten here shows up as removable rather than as a slot nothing can reach.
    const badGroup = (title, ids, rowHtml) => {
      if (!ids.length) return '';
      return '<div class="db-group"><h3 class="db-group-head db-group-head--bad">' + title + '</h3>' +
        '<div class="db-rows">' + ids.map(rowHtml).join('') + '</div></div>';
    };
    const removeBtn = id =>
      '<button type="button" class="db-btn db-remove" data-act="remove" data-card-id="' +
      esc(id) + '" title="Remove" aria-label="Remove ' + esc((C[id] && C[id].name) || id) +
      '">&minus;</button>';

    html += badGroup('NO LONGER IN THE GAME', Object.keys(seen).filter(id => !C[id]), id =>
      '<div class="db-row db-row--ghost" data-card-id="' + esc(id) + '">' +
      '<code>' + esc(id) + '</code>' + removeBtn(id) + '</div>');

    html += badGroup('CANNOT BE BUILT WITH',
      Object.keys(seen).filter(id => C[id] && TIERS.indexOf(C[id].ownershipTier) < 0), id =>
      '<div class="db-row db-row--ghost" data-card-id="' + esc(id) + '">' +
      '<span>' + esc(C[id].name) + '</span>' + removeBtn(id) + '</div>');
    el('db-deck-list').innerHTML = html || '<p class="db-empty">No cards yet. Add them from the pool.</p>';
  }

  // A tier with a floor reads as PROGRESS against it -- "Style 9/12" -- rather than as a bare
  // count. A bare 9 is only meaningful to someone who already knows the rule, which is exactly the
  // player who does not need telling.
  function renderCounts() {
    const t = R.tierCounts(DRAFT.cards);
    const mins = (R.FORMATS[FORMAT].minTiers) || {};
    el('db-count').textContent = DRAFT.cards.length + '/' + SIZE;
    const parts = TIERS.map(tier => {
      const min = mins[tier];
      const short = min && t[tier] < min;
      return '<span class="db-tier' + (short ? ' is-short' : (min ? ' is-met' : '')) + '">' +
        tierWord(tier) + ' ' + t[tier] + (min ? '/' + min : '') + '</span>';
    });
    if (t.unknown) parts.push('<span class="db-tier is-short">unknown ' + t.unknown + '</span>');
    el('db-tiers').innerHTML = parts.join(' · ');
    // The phone strip is sticky and is the only legality a player browsing the pool can see, so it
    // carries the next action rather than the tier arithmetic -- which is on the deck panel one tap
    // away, and was never readable at this width anyway.
    const next = nextActionText();
    el('db-summary-text').textContent = next
      ? DRAFT.cards.length + '/' + SIZE + ' · ' + next
      : DRAFT.cards.length + '/' + SIZE +
        ' · S' + t.Style + (mins.Style ? '/' + mins.Style : '') +
        ' F' + t.Family + (mins.Family ? '/' + mins.Family : '') +
        ' U' + t.Universal;
  }

  // WHAT TO DO NEXT, in one sentence. The counts strip says where the deck STANDS ("Style 10/12");
  // this says the shortest way to legal. The audit's finding was that legality here is descriptive
  // rather than diagnostic -- it tells you the rule you are breaking and leaves the arithmetic to
  // you, at the exact moment you are least able to do it (thirty rows into a hundred-card pool).
  //
  // THE UNMET FLOOR LEADS while the deck is short, not the size. Both are true of a 17-card deck
  // missing three Style cards, and only one of them is a step: "add 13 more cards" is the shortfall
  // restated, "add 3 more Western Boxing cards" is a thing to go and do -- and doing it moves both
  // numbers. The size line takes over once the floors are met, when any card will do.
  // Once the deck is FULL the same shortfall is a swap and says so, because "add 2 more" to a deck
  // at 30/30 is advice that cannot be followed. Everything else defers to R.validate's own lines.
  function nextActionText() {
    const n = DRAFT.cards.length;
    const t = R.tierCounts(DRAFT.cards);
    const mins = (R.FORMATS[FORMAT].minTiers) || {};
    const styleWord = DRAFT.style ? (window.STYLE_LABELS || {})[DRAFT.style] || DRAFT.style : 'Style';
    // The first unmet floor, in the order a deck is built: your own Style before its Family.
    let shortTier = null, shortBy = 0;
    ['Style', 'Family'].forEach(tier => {
      if (shortTier || !mins[tier]) return;
      if (t[tier] < mins[tier]) { shortTier = tier; shortBy = mins[tier] - t[tier]; }
    });
    const tierName = shortTier === 'Style' ? styleWord : tierWord(shortTier);

    if (!n) {
      return 'Add cards from the pool — ' + SIZE + ' in all, at least ' +
        (mins.Style || 0) + ' ' + styleWord + ' and ' + (mins.Family || 0) + ' ' + tierWord('Family') + '.';
    }
    if (n < SIZE) {
      if (shortTier) {
        return 'Add ' + shortBy + ' more ' + tierName + ' card' + (shortBy === 1 ? '' : 's') + '.';
      }
      const left = SIZE - n;
      return 'Add ' + left + ' more card' + (left === 1 ? '' : 's') + ' — any tier.';
    }
    if (shortTier) {
      return 'Swap ' + shortBy + ' card' + (shortBy === 1 ? '' : 's') + ' for ' + tierName + ' cards.';
    }
    return '';
  }

  // WHETHER THE RULE LIST IS OPEN, held in module scope rather than in the DOM. renderIssues
  // rewrites its box on every card added or removed -- which is the whole point of a live legality
  // panel -- so a <details> left to remember its own state would snap shut under the player the
  // moment they acted on what it told them. Deliberately NOT persisted: it is a reading position
  // within one editing session, not a preference, and a builder that opens tomorrow already
  // expanded has spent the space this fold exists to save.
  //
  // null means "the player has not said", and the default is then the WIDTH's answer: closed where
  // the panel is a phone overlay, open where it is a column with room. The space argument is a
  // phone argument, and folding six lines away from a desktop that was showing them fine is a
  // regression dressed as a fix. Same predicate the deck sheet uses, so the two agree about what
  // counts as narrow. Once the player has opened or closed it, their answer holds at every width.
  let ISSUES_OPEN = null;
  const issuesOpen = () => (ISSUES_OPEN === null ? !sheetIsOverlay() : ISSUES_OPEN);

  // The deck's identity folds on the same terms, and for the same reason measured one screen
  // further up: five fields of set-once data between the page title and the first card. Same null
  // = "the player has not said", same width default.
  let IDENTITY_OPEN = null;
  const identityOpen = () => (IDENTITY_OPEN === null ? !sheetIsOverlay() : IDENTITY_OPEN);

  function setIssuesOpen(open) {
    ISSUES_OPEN = !!open;
    const fold = el('db-issues').querySelector('.db-issues-fold');
    if (fold) fold.open = ISSUES_OPEN;
  }

  // LEGALITY IS A HEADLINE AND A LIST, and they have different costs. The headline -- the one
  // sentence saying what to do next -- has to be visible whenever it is true, or the strip is
  // decorative. The list behind it is one line per broken rule and runs to five or six on a deck
  // early in its build, which on a phone is most of the deck panel: the panel that exists to show
  // you your THIRTY CARDS. So the list folds, and the summary carries a count badge so that a
  // folded panel still says plainly that something is unmet and how much of it there is.
  //
  // A badge and not a dot, because the two states this has to distinguish are "one more Family
  // card" and "six separate things are wrong", and a player deciding whether to open it is
  // deciding exactly between those.
  function renderIssues() {
    const list = issues();
    const box = el('db-issues');
    if (!list.length) {
      box.className = 'db-issues is-legal';
      box.innerHTML = DRAFT.cards.length
        ? '<p class="db-issue db-issue--ok">Legal deck &mdash; ' + SIZE + ' cards.</p>'
        : '';
      return;
    }
    box.className = 'db-issues';
    // The next action leads, then the rules it is derived from. A header rather than a replacement:
    // the per-violation lines are what make it checkable, and one of them may be a copy cap or an
    // unowned card that no single sentence can summarise.
    const next = nextActionText();
    const n = list.length;
    box.innerHTML =
      '<details class="db-issues-fold"' + (issuesOpen() ? ' open' : '') + '>' +
        '<summary>' +
          // The badge is announced as a sentence rather than as a bare numeral: "3" read out on its
          // own tells a screen-reader user the count and not what it counts.
          '<span class="db-issues-badge" aria-hidden="true">' + n + '</span>' +
          '<span class="visually-hidden">' + n + ' requirement' + (n === 1 ? '' : 's') +
            ' not yet met. </span>' +
          '<span class="db-issues-next">' +
            esc(next || (n === 1 ? 'One rule left' : n + ' rules left')) + '</span>' +
        '</summary>' +
        '<div class="db-issues-body">' +
          list.map(i =>
            '<p class="db-issue" data-code="' + esc(i.code) + '">' + esc(i.message) + '</p>').join('') +
        '</div>' +
      '</details>';
    // `toggle` is safe to record from HERE and would not have been on Home, which had to listen for
    // a click instead: there the fold is an existing element whose `open` the render mutates, so
    // the render's own write fires the event and overwrites the preference with itself. This box is
    // rebuilt from a string with the attribute already in it, and an element parsed open has never
    // toggled. So the only thing that fires this is a player.
    const fold = box.querySelector('.db-issues-fold');
    fold.addEventListener('toggle', () => { ISSUES_OPEN = fold.open; });
  }

  // ---------------- the deck sheet's manners ----------------
  //
  // The sheet is a CSS transform on a body class rather than a UISheet, and that is deliberate: the
  // panel re-renders live as cards are added, which a snapshot-based sheet would fight. What it was
  // missing is everything UISheet does for free -- the off-screen panel stayed tab-reachable, focus
  // never moved into it, Escape did nothing, and the pool scrolled behind it.
  let SHEET_INVOKER = null;

  function sheetIsOverlay() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 900px)').matches);
  }

  function setSheetOpen(open, invoker) {
    SHEET_OPEN = !!open;
    if (SHEET_OPEN) SHEET_INVOKER = invoker || document.activeElement;
    render();
    const panel = el('db-deck');
    if (!panel) return;
    const toggle = document.querySelector('[data-act="toggle-sheet"]');
    if (toggle) toggle.setAttribute('aria-expanded', String(SHEET_OPEN));
    // `inert` only while it is an overlay: above 900px the deck panel is a column of the page and
    // taking the rest of the page out of the tab order would be a regression, not a fix.
    // THE FOLD, not the inner div: `inert` on #db-identity alone would leave its <summary> -- which
    // sits outside it -- tab-reachable behind an open sheet, which is the exact hole inert exists
    // to close.
    [el('db-pool'), el('db-identity-wrap') || el('db-identity')].forEach(n => {
      if (!n) return;
      if (SHEET_OPEN && sheetIsOverlay()) n.setAttribute('inert', '');
      else n.removeAttribute('inert');
    });
    if (SHEET_OPEN) {
      const close = panel.querySelector('.db-sheet-close');
      if (close && close.focus) close.focus();
    } else if (SHEET_INVOKER && document.contains(SHEET_INVOKER) && SHEET_INVOKER.focus) {
      SHEET_INVOKER.focus();
      SHEET_INVOKER = null;
    }
  }

  // Escape closes and Tab cycles, but only while the sheet is actually an overlay -- on a desktop
  // the same keys belong to the page.
  function onSheetKeydown(e) {
    if (!SHEET_OPEN || !sheetIsOverlay()) return;
    if (e.key === 'Escape') { e.preventDefault(); setSheetOpen(false); return; }
    if (e.key !== 'Tab') return;
    const panel = el('db-deck');
    if (!panel) return;
    const focusable = [].slice.call(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function renderModal() {
    const box = el('db-modal');
    if (!PENDING_STYLE) {
      box.hidden = true;
      box.innerHTML = '';
      return;
    }
    const dropped = casualtiesOf(PENDING_STYLE);
    const copies = dropped.reduce((n, d) => n + d.copies, 0);
    const kept = DRAFT.cards.length - copies;
    box.hidden = false;
    box.innerHTML =
      '<div class="db-modal-box" role="dialog" aria-modal="true" aria-labelledby="db-modal-title">' +
        '<h2 id="db-modal-title">Switch to ' + esc(label(PENDING_STYLE)) + '?</h2>' +
        '<p>' + copies + ' ' + (copies === 1 ? 'card' : 'cards') + ' cannot be played by a ' +
          esc(label(PENDING_STYLE)) + ' fighter and would be removed. ' + kept +
          ' Universal ' + (kept === 1 ? 'card stays' : 'cards stay') + '.</p>' +
        '<ul class="db-casualties">' + dropped.map(d =>
          '<li>' + esc(d.name) + (d.copies > 1 ? ' <span class="db-xn">&times;' + d.copies + '</span>' : '') +
          '</li>').join('') + '</ul>' +
        '<div class="db-modal-actions">' +
          '<button type="button" class="db-btn db-btn--ghost" data-act="style-cancel">Cancel</button>' +
          '<button type="button" class="db-btn db-btn--danger" data-act="style-confirm">Remove them and switch</button>' +
        '</div>' +
      '</div>';
  }

  function renderIdentity() {
    // WHICH DECK IS THIS, in one line, for when the fields are folded away. The name first because
    // it is what the player called it; the Style because it is the rule everything else obeys. An
    // unnamed deck says so as an instruction rather than rendering an empty summary -- the name is
    // optional to save, so this is the only place that asks for one.
    const sum = el('db-identity-sum');
    if (sum) {
      sum.textContent = (DRAFT.name || 'Name this deck') +
        (DRAFT.style ? ' — ' + label(DRAFT.style) : '');
      sum.classList.toggle('is-unnamed', !DRAFT.name);
    }
    // Written every render, because the width default has to survive a rotation and a re-entry.
    // The player's own answer, once given, is what identityOpen returns instead.
    const fold = el('db-identity-wrap');
    if (fold) fold.open = identityOpen();
    const name = el('db-name'), desc = el('db-desc'), style = el('db-style'), art = el('db-art');
    if (name.value !== DRAFT.name) name.value = DRAFT.name;
    if (desc.value !== DRAFT.description) desc.value = DRAFT.description;
    // The select shows the CURRENT style, not the pending one -- a confirm that has not been
    // accepted must leave the page describing the deck that actually exists.
    if (style.value !== DRAFT.style) style.value = DRAFT.style;
    // GROUPED BY STYLE, with the deck's own Style first. Fifteen flat options were already a long
    // list to read and the count only goes up; the grouping is also what gives new art a home
    // without a code change, and what lets art for a Style the game does not have yet still appear.
    art.innerHTML = window.fighterArtGroups(DRAFT.style).map(g =>
      '<optgroup label="' + esc(g.label) + '">' + g.keys.map(k =>
        '<option value="' + esc(k) + '"' + (k === DRAFT.artKey ? ' selected' : '') + '>' +
        esc(artLabel(k)) + '</option>').join('') + '</optgroup>').join('');
    const chosen = artTable()[DRAFT.artKey];
    const img = el('db-art-thumb');
    if (chosen && chosen.portrait) { img.src = chosen.portrait; img.alt = artLabel(DRAFT.artKey); img.hidden = false; }
    else { img.hidden = true; }
  }

  // ---------------- how much height the two panels may have ----------------
  //
  // WHAT THIS ANSWERS. styles.css caps #db-pool and #db-deck at
  // `max(var(--db-deck-floor, 260px), calc(100vh - var(--db-chrome, 210px)))`, and this is what
  // publishes both of those. 210px was a constant and it was wrong by 216px at 1280 wide, which
  // pushed the Save button, the validation issues and both disclosures below the fold on a page
  // whose panels scroll independently so that exactly that would not happen. The rule and the
  // reasoning live in the stylesheet beside the cap; this is the getBoundingClientRect half.
  //
  // MEASURED, NOT DERIVED, and the same choice js/page-nav.js records for --sc-site-bar-h: the
  // rules blurb in #top-bar rewraps between two and four lines across the desktop range, the
  // identity row rewraps under it, the account panel appears only once a backend has answered,
  // and all three disclosures can be opened. No constant is right in more than one of those.
  //
  // ONE PASS IS ENOUGH, which is worth stating because a height budget that reads the layout it
  // then changes usually is not. Both terms are independent of what the panels do with the
  // answer: everything in `above` is laid out before the columns, and `below` is a difference of
  // two bottom edges, so it measures the disclosures' own height rather than where they sit.
  // Nothing here can feed back into itself, and there is no observer for the same reason.
  //
  // Returns 0 without touching anything when there is nothing to measure -- the start gate,
  // where #db-columns is inside a hidden screen, and jsdom, where every rect is zeroes. A suite
  // that runs this must not be handed a chrome height of 14px and a page capped at one viewport
  // minus nothing.
  const CHROME_GUTTER = 14;            // the page's own padding-inline, as breathing room below
  // What is left of the deck panel when the card list has shrunk to nothing. #db-deck is
  // `overflow: hidden` around a flex column whose only flexible child is #db-deck-list, so a cap
  // below this number does not squeeze the list -- the list is already at zero -- it clips the
  // issue list, the buttons and the status line straight out of the panel. That is the deck's
  // Save button, present and unreachable, which is worse than a page that scrolls.
  const LIST_MINIMUM = 44;             // ...plus enough list left to read as a list
  function deckFurniture() {
    const deck = el('db-deck'), list = el('db-deck-list'), last = el('db-status');
    if (!deck || !list || !last) return 0;
    // Measured from real laid-out positions rather than by summing heights, because the margins
    // between these rows are part of what has to fit and a sum of border boxes leaves them out.
    // Clipping does not affect a rect, so this reads correctly on a panel that is already
    // overflowing -- which is exactly when the answer matters.
    const d = deck.getBoundingClientRect();
    const cs = window.getComputedStyle(deck);
    const tail = parseFloat(cs.paddingBottom) + parseFloat(cs.borderBottomWidth);
    const h = (last.getBoundingClientRect().bottom - d.top) -
              list.getBoundingClientRect().height + (tail || 0);
    return h > 0 ? Math.ceil(h) : 0;
  }
  function measurePanels() {
    // A page that is going away still fires resize and toggle at whatever is listening, and
    // `document` is gone by then. Nothing here is worth a throw on the way out; the caller's
    // 0 already means "could not measure".
    if (typeof document === 'undefined' || !document) return 0;
    const cols = el('db-columns'), app = document.getElementById('app');
    if (!cols || !app || typeof cols.getBoundingClientRect !== 'function') return 0;
    const c = cols.getBoundingClientRect(), a = app.getBoundingClientRect();
    if (!(c.height > 0) || !(a.height > 0)) return 0;
    const above = c.top + (window.scrollY || 0);
    const below = Math.max(0, a.bottom - c.bottom);
    const chrome = Math.round(above + below + CHROME_GUTTER);
    document.documentElement.style.setProperty('--db-chrome', chrome + 'px');
    // Neither of these feeds back into itself, which is what makes one pass enough. Everything in
    // `above` is laid out before the columns; `below` is a difference of two bottom edges, so it
    // is the disclosures' height rather than their position; and the furniture is measured with
    // the list's height subtracted, so it does not move when the cap it produces resizes the list.
    const floor = deckFurniture();
    if (floor > 0) {
      document.documentElement.style.setProperty('--db-deck-floor', (floor + LIST_MINIMUM) + 'px');
    }
    return chrome;
  }

  // ---- WHAT THE STYLE YOU ARE CHOOSING ACTUALLY IS ----------------------------------------------
  //
  // Phase H of the after-the-bell plan, from the 2026-09-05 persona review. This page asks "which
  // Style?" before anything else -- the answer fixes 12 of the 30 slots outright and the Foundation
  // that supplies 8 more -- and the only thing it said about the choice was that it decides your
  // colour and which cards you may use.
  //
  // BOTH HALVES COME FROM MODULES THIS PAGE DID NOT LOAD. js/style-passives.js was written
  // dependency-free in so many words "so library.html and deck-builder.html can load it", and then
  // neither ever did -- so the Foundation passive, the free rule that most defines how a Style
  // plays, was unreachable on the one screen whose first question is which Style to take.
  //
  // hudHtml rather than a local composition, for the reason js/ui.js gives at deckStyleLineHtml:
  // one renderer, so the builder and the bout cannot word the same passive differently. Both
  // modules read through guards, so this page still works with either absent.
  // TWO LENGTHS, AND THE DIFFERENCE IS WHICH QUESTION IS STILL OPEN.
  //
  // On the START GATE the Style has not been chosen, so the whole brief is the point -- that screen
  // exists to answer "which of these do I want?" and has the room, because it is a two-column gate
  // with nothing else in it.
  //
  // In the OPEN DRAFT the Style is already settled and cannot be changed without a confirm. What is
  // still useful there is the Foundation passive -- the always-on rule the deck is being built
  // around, and the thing the header above asks about ("8 Foundation cards") without ever naming --
  // plus one line of range. The gameplan paragraph would be re-answering a closed question, and
  // measured it pushed the builder page 44px past one viewport at 1440x900, which the browser
  // ledger caught as two new failures. Height on that page is paid for by the card pool.
  function styleBriefHtml(style, opts) {
    if (!style) return '';
    const full = !!(opts && opts.full);
    const SB = window.StyleBriefs, SP = window.StylePassives;
    const brief = (SB && SB.get) ? SB.get(style) : null;
    const passive = (SP && SP.forFighter) ? SP.forFighter(style) : null;
    if (!brief && !passive) return '';
    let out = '';
    if (passive && SP.hudHtml) {
      out += '<p class="db-brief-passive">' + SP.hudHtml(style) + '</p>';
    }
    if (brief) {
      out += '<p class="db-brief-range"><span class="db-brief-key">Range</span> ' +
             esc(brief.range) + '</p>';
      if (full) out += '<p class="db-brief-plan">' + esc(brief.gameplan) + '</p>';
    }
    return out;
  }

  // Both copies: the start gate's, which follows its own select, and the open draft's, which
  // follows DRAFT.style. Null-safe on the elements because the two screens are never both mounted.
  function renderStyleBriefs() {
    const startSel = el('db-start-style');
    const start = el('db-start-brief');
    if (start) start.innerHTML = styleBriefHtml(startSel && startSel.value, { full: true });
    const draft = el('db-style-brief');
    if (draft) draft.innerHTML = styleBriefHtml(DRAFT.style);
  }

  function render() {
    // Before the early return: the saved list belongs to neither screen and shows on both, so a
    // returning player meets their decks on the start gate rather than having to begin a new one to
    // find the old ones.
    renderSavedList();
    // Before the early return too: the start gate is the screen BEHIND that return, and its brief
    // is the whole reason this call exists.
    renderStyleBriefs();
    if (!DRAFT.style) {
      el('db-start').hidden = false;
      el('db-builder').hidden = true;
      return;
    }
    el('db-start').hidden = true;
    el('db-builder').hidden = false;
    document.body.classList.toggle('db-sheet-open', SHEET_OPEN);
    renderIdentity();
    renderPool();
    renderBuyHint();
    renderDeck();
    renderCounts();
    renderIssues();
    renderModal();
    const legal = isLegal();
    el('db-save').disabled = !legal;
    el('db-save-play').disabled = !legal;
    // Share follows legality too, and that is not tidiness. encodeShareCode will happily encode a
    // 17-card draft, but decodeShareCode validates -- so an incomplete deck would produce a code
    // that looks fine, travels fine, and fails on the other person's screen. A code that cannot be
    // imported should not be issuable.
    el('db-share').disabled = !legal;
    // Delete is about the SAVED deck, not the draft, so it turns on only once there is something in
    // storage to delete -- a Delete button on an unsaved draft has nothing to act on.
    el('db-delete').disabled = !DRAFT.id;
    el('db-status').textContent = STATUS;
    // Last, because it measures what everything above it just drew.
    measurePanels();
  }

  // ---------------- events ----------------
  //
  // Delegated rather than inline onclick attributes. The rows are rebuilt on every render, so
  // per-element listeners would have to be re-attached each time; and an inline handler would put
  // this module's names into an HTML string, where nothing resolves them until a click (which is
  // the failure tests/ui-handler-names-test.js exists to catch on the game side).
  function onClick(e) {
    const btn = e.target.closest('[data-act]');
    // A TILE IS A CONTROL NOW, and it is routed here rather than through an inline onclick for the
    // reason stated above: this module's names do not belong in an HTML string. compactCardHtml
    // already stamps `data-card-id` on the tile it returns, so the tile needs no attribute of its
    // own -- and checking it only after `[data-act]` has missed is what keeps the + and - working,
    // since those sit OUTSIDE the tile and would otherwise never reach this branch anyway.
    if (!btn) {
      const tile = e.target.closest('.ccard[data-card-id]');
      if (tile) openCardSheet(tile.getAttribute('data-card-id'));
      return;
    }
    const act = btn.getAttribute('data-act');
    const id = btn.getAttribute('data-card-id');
    if (act === 'add') addCard(id);
    // Not an add that failed -- an add that was never possible, answered with the thing that would
    // make it possible. STATUS first, because setSheetOpen renders and the line has to be in the
    // panel it opens rather than one render behind it. The invoker is passed so Escape puts the
    // player back on the + they pressed, thirty rows down the pool.
    else if (act === 'add-full') {
      STATUS = 'Deck is full — remove a card to make room for ' +
               ((cardsTable()[id] || {}).name || 'this card') + '.';
      setSheetOpen(true, btn);
    }
    // The start gate's clone rows. Through the delegated router like everything else here, and for
    // the reason stated above it: the rows are rebuilt on every render, so a listener bound to a
    // row is a listener bound to an element that is about to be replaced.
    else if (act === 'gate-clone') cloneFrom(btn.getAttribute('data-deck-id'));
    else if (act === 'remove') removeCard(id);
    else if (act === 'style-confirm') confirmStyleChange();
    else if (act === 'style-cancel') cancelStyleChange();
    else if (act === 'clear-filters') {
      FILTERS.search = ''; FILTERS.tier = 'all'; FILTERS.range = 'all';
      FILTERS.speed = 'all'; FILTERS.category = 'all';
      syncFilterControls();
      render();
    // `btn`, not the invoker name this line used to carry: it read `t`, which is declared nowhere
    // in this module, so every tap threw a ReferenceError under the IIFE's `use strict` and the
    // sheet never opened. Below 900px the deck panel IS this overlay, so a phone player could not
    // open their deck or reach Save at all -- and nothing caught it, because the suite drove
    // setSheetOpen directly and never the click that routes to it.
    } else if (act === 'toggle-sheet') { setSheetOpen(!SHEET_OPEN, btn); }
    else if (act === 'save' || act === 'save-play') attemptSave(act === 'save-play');
    else if (act === 'delete') deleteDraft();
    else if (act === 'open-saved') openSaved(btn.getAttribute('data-deck-id'));
    // The list's delete, distinct from 'delete' above: that one acts on the deck that is OPEN,
    // these act on a deck where it is LISTED. `data-deck-id`, not the card id read at the top.
    else if (act === 'delete-saved') requestDeleteSaved(btn.getAttribute('data-deck-id'));
    else if (act === 'delete-saved-confirm') confirmDeleteSaved();
    else if (act === 'delete-saved-cancel') cancelDeleteSaved();
    else if (act === 'share') exportCode();
    else if (act === 'copy-code') copyCode();
    else if (act === 'copy-link') copyLink();
    else if (act === 'import') importCode();
    // A full navigation rather than a fetch: OAuth is a redirect flow, and the browser has to
    // actually go to Discord for the player to see whose consent screen they are on.
    else if (act === 'account-signin') window.location.href = window.DeckSync.SIGN_IN_URL;
    else if (act === 'account-sync') runSync(false);
    else if (act === 'account-signout') signOutAccount();
    else if (act === 'account-delete') { ACCOUNT.confirmDelete = true; ACCOUNT.message = ''; renderAccount(); }
    else if (act === 'account-delete-cancel') { ACCOUNT.confirmDelete = false; renderAccount(); }
    else if (act === 'account-delete-confirm') deleteAccount();
  }

  async function deleteAccount() {
    if (!accountReady() || ACCOUNT.busy) return;
    ACCOUNT.busy = true; ACCOUNT.message = ''; renderAccount();
    let res;
    try { res = await window.DeckSync.deleteAccount(); }
    catch (e) { res = { ok: false, error: 'unexpected' }; }
    ACCOUNT.busy = false;
    ACCOUNT.confirmDelete = false;
    if (res && res.ok) {
      ACCOUNT.signedIn = false;
      // The count comes from the SERVER's own report of what it removed, not from what the client
      // believed was up there. Telling someone "3 decks deleted" from a stale local guess is the
      // one number in this flow they cannot check.
      ACCOUNT.message = 'Account deleted' +
        (res.decksRemoved ? ' — ' + res.decksRemoved + ' deck' + (res.decksRemoved === 1 ? '' : 's') +
         ' removed from it' : '') +
        '. Your decks in this browser are untouched.';
    } else {
      ACCOUNT.message = 'Could not delete the account just now. Nothing was changed.';
    }
    renderAccount();
  }

  async function signOutAccount() {
    if (!accountReady()) return;
    ACCOUNT.busy = true; renderAccount();
    try { await window.DeckSync.signOut(); } catch (e) { /* the device is cleared either way */ }
    ACCOUNT.busy = false;
    ACCOUNT.signedIn = false;
    // THE DECKS STAY. Signing out ends the session, it does not take away what is on this device --
    // deleting a player's local decks because they signed out would be the feature destroying the
    // thing it exists to protect.
    ACCOUNT.message = 'Signed out. Your decks are still here on this device.';
    renderAccount();
  }

  // Saving reports its own outcome in words. Every failure the store can return -- storage refused,
  // twenty decks already saved, quota exhausted mid-write -- is a real thing a player can hit and a
  // different thing for them to do about it, so the message comes from the store rather than being
  // flattened here into "could not save".
  function attemptSave(thenPlay) {
    if (!isLegal()) return;
    const store = window.DeckStore;
    if (!store) { STATUS = 'Deck storage is unavailable in this build.'; render(); return; }
    const res = store.save(DRAFT);
    if (!res.ok) { STATUS = res.message; render(); return; }
    DRAFT.id = res.id;
    STATUS = 'Saved.';
    render();
    // Save & play hands the deck to the game by ID rather than by value: the store has already
    // written it, and index.html merges it at boot, so the only thing that has to travel is which
    // one to preselect.
    if (thenPlay) window.location.href = 'index.html?deck=' + encodeURIComponent(res.id);
  }

  function deleteDraft() {
    const store = window.DeckStore;
    if (!store || !DRAFT.id) return;
    if (!store.get(DRAFT.id)) {
      // Already gone -- deleted from the list below, or by a sync from another device, while it
      // sat open here. The draft's claim on the stored deck ends either way, and saying so beats
      // a Delete button that appears to do nothing.
      DRAFT.id = null;
      STATUS = 'That deck is no longer saved. This draft is still open — save it to keep it.';
      render();
      return;
    }
    if (!store.remove(DRAFT.id)) {
      // The write failed with the deck still in storage. "Deleted." here would be the page lying
      // about the one thing this button exists to do, so the message follows the return value.
      STATUS = 'Could not delete — this browser refused the write. The deck is still saved.';
      render();
      return;
    }
    DRAFT.id = null;
    STATUS = 'Deleted. This draft is still open — save it to keep it.';
    render();
  }

  // ---------------- deleting from the saved list ----------------
  //
  // deleteDraft above acts on the deck that is OPEN; these act on a deck where it is LISTED. The
  // list is the only place EVERY saved deck can be reached -- including one whose record is too
  // broken to open -- so it is where delete has to live for the store's own limit message
  // ("Delete one to save another.") to be an instruction rather than a riddle.
  //
  // The confirm is a second state of the row rather than a confirm() dialog -- the account panel's
  // pattern, for the account panel's reason: the warning stays on screen while the decision is
  // made. One id at a time, like PENDING_STYLE: asking about a second deck moves the question.
  function requestDeleteSaved(id) {
    const store = window.DeckStore;
    if (!store || !store.get(id)) return false;
    PENDING_DELETE = id;
    SAVED_MSG = '';
    render();
    // Focus goes to KEEP, not Delete. The control that was under the player's finger has just been
    // rebuilt into a question, and the safe reading of a stray second Enter is "I did not mean it".
    const keep = document.querySelector('[data-act="delete-saved-cancel"]');
    if (keep && keep.focus) keep.focus();
    return true;
  }

  function cancelDeleteSaved() {
    const id = PENDING_DELETE;
    if (!id) return false;
    PENDING_DELETE = null;
    render();
    // Back to the control that asked, so a keyboard player is not dropped to the top of the page.
    const del = document.querySelector('[data-act="delete-saved"][data-deck-id="' + id + '"]');
    if (del && del.focus) del.focus();
    return true;
  }

  function confirmDeleteSaved() {
    const store = window.DeckStore;
    const id = PENDING_DELETE;
    if (!store || !id) return false;
    const record = store.get(id);
    if (!record) {
      // Gone between the ask and the answer -- a sync can do that. Not a failure to report as one:
      // the deck the player wanted deleted is deleted.
      PENDING_DELETE = null;
      SAVED_MSG = 'That deck is already gone.';
      render();
      return false;
    }
    const name = record.name || id;
    const removed = store.remove(id);
    PENDING_DELETE = null;
    if (!removed) {
      // Same obligation as deleteDraft: remove() answering false means the deck is still in
      // storage, and the page must not say otherwise.
      SAVED_MSG = 'Could not delete "' + name + '" — this browser refused the write. The deck is still saved.';
      render();
      return false;
    }
    if (DRAFT.id === id) {
      // The open draft WAS this deck. Same convergence as deleteDraft: the draft on screen is the
      // undo, and the cleared id means a later Save writes a new deck rather than resurrecting the
      // slot of the one just deleted.
      DRAFT.id = null;
      STATUS = 'Deleted. This draft is still open — save it to keep it.';
    }
    SAVED_MSG = 'Deleted "' + name + '".';
    render();
    // The focused element just removed itself with the row, so focus is placed by hand on the list
    // box (tabindex="-1" in the markup) rather than left to fall to <body>.
    const box = el('db-saved');
    if (box && box.focus) box.focus();
    return true;
  }

  // Load a saved deck for editing or repair. A deck that needs repair opens exactly as saved,
  // including the ids that no longer resolve: the builder renders those in their own group so a
  // player can see what went missing and replace it, which is the whole reason the store keeps a
  // broken deck instead of tidying it away.
  function openSaved(id) {
    const store = window.DeckStore;
    const record = store && store.get(id);
    if (!record) return false;
    DRAFT.id = record.id;
    DRAFT.name = record.name;
    DRAFT.description = record.description || '';
    // A BAD_RECORD has no card array at all, and .slice() on it used to throw -- which, with the
    // old delete requiring the deck to be OPEN first, made such a record permanently unremovable.
    // It opens as an empty draft instead: Save demands a legal 30, so an accidental overwrite of
    // the stored record cannot happen on the way to repairing it.
    DRAFT.cards = Array.isArray(record.cards) ? record.cards.slice() : [];
    DRAFT.artKey = record.artKey;
    DRAFT.style = record.style;
    PENDING_STYLE = null;
    PENDING_DELETE = null;
    SAVED_MSG = '';
    STATUS = '';
    paintAccent(record.style);
    render();
    return true;
  }

  // ---------------- share codes ----------------

  // Produce this draft's code and show it. Shown in a field rather than only copied: clipboard
  // access can be refused, and a code the player can see and select by hand always works.
  function exportCode() {
    const res = R.encodeShareCode(DRAFT);
    const box = el('db-export');
    const msg = el('db-export-msg');
    el('db-share-wrap').open = true;
    if (!res.ok) {
      box.hidden = true;
      msg.textContent = res.message;
      msg.className = 'db-share-msg is-bad';
      return res;
    }
    box.hidden = false;
    el('db-export-code').value = res.code;
    msg.textContent = res.code.length + ' characters. Anyone who pastes this gets your deck.';
    msg.className = 'db-share-msg';
    return res;
  }

  function copyCode() {
    const input = el('db-export-code');
    const msg = el('db-export-msg');
    input.select();
    const done = () => { msg.textContent = 'Copied.'; msg.className = 'db-share-msg is-ok'; };
    const failed = () => {
      msg.textContent = 'This browser would not copy it — select the text above and copy it yourself.';
      msg.className = 'db-share-msg is-bad';
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(input.value).then(done, failed);
      } else failed();
    } catch (e) { failed(); }
  }

  // ---- the same deck, as a link -------------------------------------------------------------
  //
  // deploy_handoff/retention-plan.md R4. Share codes have worked end to end since the builder
  // shipped, and RECEIVING one is four steps: open the builder, find Share codes, click the field,
  // paste, Import. The code is the durable form — it survives a chat client that eats URLs, and it
  // can be read out — so this adds a second form rather than replacing it.
  //
  // RELATIVE TO location.href, NEVER A HARDCODED ORIGIN. A file:// copy of this game is a supported
  // way to play it (the whole repo runs with no build step and no server), and a link minted there
  // has to be a file:// link that actually opens the file beside it. The deployed site mints its
  // own origin by the same rule, and a preview deployment mints the preview's — which is what makes
  // a link tested on preview a link that was really tested.
  function shareLink(code) {
    const c = code != null ? code : (el('db-export-code') ? el('db-export-code').value : '');
    if (!c) return '';
    try {
      return new URL('deck-builder.html?code=' + encodeURIComponent(c), location.href).toString();
    } catch (e) { return ''; }
  }

  function copyLink() {
    const msg = el('db-export-msg');
    const url = shareLink();
    const failed = () => {
      // A DIFFERENT SENTENCE FROM copyCode'S, because the fallback it offers is different: the code
      // is in a field the player can select, and the link is not on screen anywhere. So this one
      // hands them the other button rather than pointing at text they cannot see.
      msg.textContent = 'This browser would not copy it — use Copy above and send the code instead.';
      msg.className = 'db-share-msg is-bad';
    };
    if (!url) { failed(); return; }
    const done = () => { msg.textContent = 'Link copied.'; msg.className = 'db-share-msg is-ok'; };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, failed);
      } else failed();
    } catch (e) { failed(); }
  }

  // A CODE THAT ARRIVED IN THE URL. Read once, at init, after the first render.
  //
  // STRIPPED BEFORE THE IMPORT CAN FAIL, and the order is the point rather than tidiness: this
  // function's whole job is to make sure a RELOAD does not replay an import over whatever the
  // player has built since, and a strip that happens after a throw is a strip that did not happen.
  // Same shape as the signin_error handling in initAccount below — replaceState, other params kept.
  //
  // And a bookmark of the RESULT should be the builder, not the code: a player who opens a friend's
  // link, edits the deck for twenty minutes and hits reload must not be handed the friend's deck
  // back.
  //
  // AFTER render(), because importCode writes its outcome into #db-import-msg and reads the paste
  // field, both of which the first render puts on the page. A malformed code shows decodeShareCode's
  // own message, unchanged — it is the same import path as the paste box, so there is one set of
  // error sentences and one set of draft semantics ("opened, not saved") rather than two.
  function importFromUrl() {
    let code = null, q = null;
    try {
      q = new URLSearchParams(window.location.search);
      code = q.get('code');
    } catch (e) { return null; }        // a URL we cannot parse carries no code we can trust
    if (!code) return null;

    // THE STRIP IS BEST-EFFORT AND ITS OWN try, which is not the same thing as the read's. A
    // BROWSER CAN REFUSE THIS: on a file:// page replaceState is a cross-origin history update as
    // far as some engines are concerned, and it throws SecurityError — and a file:// copy of this
    // game is a supported way to play it. Wrapping the strip and the import together was the first
    // version, and it meant the one environment that refuses the rewrite also silently refused the
    // IMPORT, which is the thing the player actually asked for. So: try to clean the URL, then
    // open the deck either way. Where the rewrite is refused a reload replays the import, which is
    // the lesser of the two failures and is confined to local copies.
    try {
      q.delete('code');
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? '?' + rest : ''));
    } catch (e) { /* the URL keeps the code; the import below still runs */ }

    const res = importCode(code);
    // The share panel is a <details> and it is closed by default, so a code that failed would put
    // its explanation inside a folded box. Opened either way: on success it shows "Opened ... not
    // saved yet", which is the sentence that tells a player the deck is theirs to keep or discard.
    const wrap = el('db-share-wrap');
    if (wrap) wrap.open = true;
    return res;
  }

  // Import opens the decoded deck as an UNSAVED draft. Never auto-saved: a code is something a
  // player received, and writing it into their collection before they have looked at it takes the
  // decision away and spends one of their twenty slots.
  function importCode(text) {
    const msg = el('db-import-msg');
    const res = R.decodeShareCode(text != null ? text : el('db-import-code').value);
    if (!res.ok) {
      msg.textContent = res.message;
      msg.className = 'db-share-msg is-bad';
      return res;
    }
    const d = res.deck;
    DRAFT.id = null;                      // a NEW draft, not a claim on anyone's saved deck
    DRAFT.name = d.name;
    DRAFT.description = d.description;
    DRAFT.cards = d.cards.slice();
    DRAFT.style = d.style;
    DRAFT.artKey = d.artKey || defaultArtFor(d.style);
    PENDING_STYLE = null;
    PENDING_DELETE = null;
    SAVED_MSG = '';
    STATUS = 'Imported. This deck is not saved yet.';
    msg.textContent = 'Opened "' + d.name + '". It is not saved yet.';
    msg.className = 'db-share-msg is-ok';
    paintAccent(d.style);
    render();
    return res;
  }

  function renderSavedList() {
    const store = window.DeckStore;
    const box = el('db-saved');
    if (!store) { box.innerHTML = ''; return; }
    if (!store.available()) {
      box.innerHTML = '<p class="db-empty db-empty--warn">This browser will not store decks. ' +
        'Your work stays on screen but will not survive a reload.</p>';
      return;
    }
    const saved = store.list();
    // role="status" because the row this line reports on has usually just removed itself, and a
    // screen-reader user should hear the outcome without hunting for it.
    const msg = SAVED_MSG ? '<p class="db-saved-msg" role="status">' + esc(SAVED_MSG) + '</p>' : '';
    if (!saved.length) { box.innerHTML = '<p class="db-empty">No saved decks yet.</p>' + msg; return; }
    box.innerHTML = '<ul class="db-saved-list">' + saved.map(entry => {
      const r = entry.record || {};
      const broken = !entry.playable;
      const name = r.name || entry.id;
      // The row that is being asked about shows the QUESTION in place of its controls -- open is
      // withheld while it asks, because a row that both asks and offers to open is asking two
      // things at once. Delete leads Keep to match the account panel's confirm ordering.
      if (entry.id === PENDING_DELETE) {
        return '<li class="db-saved-item is-confirm' + (broken ? ' is-broken' : '') + '">' +
          '<span class="db-saved-q">Delete &ldquo;' + esc(name) +
            '&rdquo;? This cannot be undone on this device.</span>' +
          '<span class="db-saved-acts">' +
            '<button type="button" class="db-btn is-danger" data-act="delete-saved-confirm">Delete</button>' +
            '<button type="button" class="db-btn" data-act="delete-saved-cancel">Keep</button>' +
          '</span></li>';
      }
      const note = broken
        ? (entry.issues[0] ? entry.issues[0].message : 'needs repair')
        : (window.STYLE_LABELS[r.style] || r.style);
      // The aria-label carries the deck's name because every row's visible delete says the same
      // word, and "Delete" read out eight times is not a list a keyboard user can choose from.
      return '<li class="db-saved-item' + (broken ? ' is-broken' : '') + '">' +
        '<button type="button" class="db-link" data-act="open-saved" data-deck-id="' + esc(entry.id) +
          '">' + esc(name) + '</button>' +
        '<span class="db-saved-note">' + esc(note) + '</span>' +
        '<button type="button" class="db-btn db-btn--ghost db-saved-del" data-act="delete-saved" ' +
          'data-deck-id="' + esc(entry.id) + '" aria-label="Delete ' + esc(name) + '">Delete</button>' +
        '</li>';
    }).join('') + '</ul>' + msg;
  }

  function syncFilterControls() {
    el('db-search').value = FILTERS.search;
    el('db-filter-tier').value = FILTERS.tier;
    el('db-filter-range').value = FILTERS.range;
    el('db-filter-speed').value = FILTERS.speed;
    el('db-filter-category').value = FILTERS.category;
  }

  function buildStaticControls() {
    el('db-style').innerHTML = window.STYLES.map(s =>
      '<option value="' + esc(s) + '">' + esc(label(s)) + '</option>').join('');
    el('db-start-style').innerHTML = '<option value="">Choose a Style…</option>' + window.STYLES.map(s =>
      '<option value="' + esc(s) + '">' + esc(label(s)) + '</option>').join('');
    // Cloning is the one control here that reads a whole deck's list into the draft, so a concealed
    // deck in this menu would not merely be selectable -- it would print its thirty cards into the
    // builder's own column, which is a fuller preview than the setup screen ever offered.
    //
    // BOTH CONTROLS READ openDeckIds(), and that is the whole of the rule: the shapes below differ
    // because the two moments differ, the SOURCE never does. A future list built from
    // Object.keys(window.DECKS) would look identical and leak the season's authored bosses.
    const cloneIds = window.DeckRules.openDeckIds();
    const cloneOpts = cloneIds.map(k =>
      '<option value="' + esc(k) + '">' + esc(window.DECK_META[k].name) + ' — ' +
      esc(label(window.DECK_META[k].style)) + '</option>').join('');
    // The builder screen keeps its select: it is a control on a working screen, competing for room
    // with a thirty-card column, and its reader has already chosen a deck once.
    el('db-clone').innerHTML = '<option value="">Clone a deck…</option>' + cloneOpts;

    // THE GATE GETS ROWS, because the gate is a menu rather than a field. Its reader has chosen
    // nothing yet, and the question they are actually asking -- "which of these do I want?" -- is
    // answered by the archetype sentence, which an <option> has nowhere to put.
    //
    // `goodFirst` is the same flag the setup screen's deck options surface (js/ui.js), so the two
    // screens recommend the same decks by construction rather than by two lists agreeing. Where a
    // deck carries no archetype the row simply has one line: an absent sentence is not an error,
    // and inventing filler for it would be worse than the name alone.
    // The start gate's Style select answers as it is scrolled, not only once something is built.
    const startStyle = el('db-start-style');
    if (startStyle) startStyle.addEventListener('change', renderStyleBriefs);
    el('db-start-clone').innerHTML = cloneIds.map(k => {
      const m = window.DECK_META[k];
      return '<button type="button" class="db-clone-row" data-act="gate-clone" data-deck-id="' +
        esc(k) + '">' +
        '<span class="db-clone-head"><b>' + esc(m.name) + '</b>' +
        '<span class="db-clone-style">' + esc(label(m.style)) + '</span>' +
        (m.goodFirst ? '<span class="db-clone-first">Good first deck</span>' : '') + '</span>' +
        (m.archetype ? '<span class="db-clone-what">' + esc(m.archetype) + '</span>' : '') +
        '</button>';
    }).join('');

    // Category counts are printed in the option labels because HALF THE POOL HAS NO CATEGORY: 97 of
    // 194 cards carry no attack.category at all. A filter that silently hides 97 cards behind an
    // innocuous-looking select is a filter that makes the pool look smaller than it is, so the
    // numbers are on the options and "Uncategorised" is a choice rather than a void.
    const C = cardsTable();
    const catCount = {};
    Object.keys(C).forEach(id => {
      const c = (C[id].attack && C[id].attack.category) || 'none';
      catCount[c] = (catCount[c] || 0) + 1;
    });
    const cats = Object.keys(catCount).filter(c => c !== 'none').sort();
    el('db-filter-category').innerHTML =
      '<option value="all">Any category</option>' +
      cats.map(c => '<option value="' + esc(c) + '">' + esc(c) + ' (' + catCount[c] + ')</option>').join('') +
      '<option value="none">Uncategorised (' + (catCount.none || 0) + ')</option>';
    el('db-filter-speed').innerHTML = '<option value="all">Any speed</option>' +
      [3, 2, 1].map(n => '<option value="' + n + '">' + SPEED_WORD[n] + '</option>').join('');
  }

  function wire() {
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onSheetKeydown);

    // ON THE SUMMARY'S CLICK, NOT ON `toggle`. renderIdentity writes `open` on this element every
    // render, and the toggle event does not distinguish that write from a player -- it would record
    // the default as if it were a choice, one render after the choice was made. `click` fires
    // before the browser applies the default action, so `!fold.open` is the state it is moving to.
    // Keyboard activation of a summary dispatches a click too, so this is not a mouse-only path.
    const idFold = el('db-identity-wrap');
    if (idFold) {
      idFold.querySelector('summary').addEventListener('click', () => {
        IDENTITY_OPEN = !idFold.open;
      });
    }
    el('db-name').addEventListener('input', e => { DRAFT.name = e.target.value.slice(0, R.NAME_MAX); });
    el('db-desc').addEventListener('input', e => { DRAFT.description = e.target.value.slice(0, R.DESC_MAX); });
    el('db-style').addEventListener('change', e => {
      const wanted = e.target.value;
      const res = requestStyle(wanted);
      // Put the select back to the deck's real Style while a confirm is open, so the page never
      // describes a Style the deck does not have.
      if (!res.changed) e.target.value = DRAFT.style;
    });
    el('db-art').addEventListener('change', e => {
      DRAFT.artKey = e.target.value;
      render();
    });
    el('db-clone').addEventListener('change', e => {
      if (e.target.value) { cloneFrom(e.target.value); e.target.value = ''; }
    });
    // (No listener for #db-start-clone: it is a list of delegated buttons now, not a select. See
    // the `gate-clone` branch in onClick.)
    el('db-start-style').addEventListener('change', e => {
      if (e.target.value) newDeck(e.target.value);
    });

    el('db-search').addEventListener('input', e => { FILTERS.search = e.target.value.trim(); renderPool(); });
    [['db-filter-tier', 'tier'], ['db-filter-range', 'range'],
     ['db-filter-speed', 'speed'], ['db-filter-category', 'category']].forEach(pair => {
      el(pair[0]).addEventListener('change', e => { FILTERS[pair[1]] = e.target.value; renderPool(); });
    });
  }

  // ---------------- the account panel ----------------
  //
  // Everything here is additive and nothing in the builder waits on it. The panel starts hidden in
  // the markup and only ever unhides, so the page is fully usable before -- and if -- the network
  // answers. A player with no account, no connection, or a `file://` copy of the game sees exactly
  // the builder that shipped before any of this existed.
  const ACCOUNT = { configured: false, signedIn: false, busy: false, message: '', confirmDelete: false };

  function accountReady() {
    return !!(window.DeckSync && window.DeckStore && window.DeckStore.available());
  }

  function renderAccount() {
    const wrap = el('db-account-wrap');
    if (!wrap) return;
    if (!ACCOUNT.configured) { wrap.hidden = true; return; }
    wrap.hidden = false;

    const last = window.DeckSync.lastSyncedAt();
    el('db-account-state').textContent = ACCOUNT.signedIn
      ? 'Signed in. Your decks follow you to any device.'
      : 'Your decks are saved in this browser only. Sign in to carry them between devices.';

    const btn = (act, label, cls) =>
      '<button type="button" class="db-btn' + (cls ? ' ' + cls : '') + '" data-act="' + act + '"' +
      (ACCOUNT.busy ? ' disabled' : '') + '>' + esc(label) + '</button>';
    // THE CONFIRM IS A SECOND STATE, not a confirm() dialog. It renders in place, so the warning
    // about what is and is not about to be deleted is on screen while the decision is made rather
    // than in a modal that has to summarise it in one line.
    el('db-account-actions').innerHTML = !ACCOUNT.signedIn
      ? btn('account-signin', 'Sign in with Discord')
      : ACCOUNT.confirmDelete
        ? btn('account-delete-confirm', 'Yes, delete my account', 'is-danger') +
          btn('account-delete-cancel', 'Keep my account')
        : btn('account-sync', ACCOUNT.busy ? 'Syncing…' : 'Sync now') +
          btn('account-signout', 'Sign out') +
          btn('account-delete', 'Delete my account', 'is-quiet');

    // WHAT IS STORED, SHOWN BEFORE SIGNING IN and kept visible after. A privacy note that only
    // appears once consent has already been given is not a notice, it is an apology.
    const priv = el('db-account-privacy');
    if (priv) {
      priv.textContent = ACCOUNT.confirmDelete
        ? 'This removes your account and the decks stored in it, permanently. ' +
          'The decks saved in this browser are NOT deleted and will still be here.'
        : (window.DeckSync ? window.DeckSync.PRIVACY_NOTE : '');
      priv.classList.toggle('is-warning', ACCOUNT.confirmDelete);
    }

    // The last-synced line is the difference between "this is working" and "I have no idea whether
    // this is working", which for a feature whose whole job is invisible is the entire interface.
    const when = last ? new Date(last) : null;
    el('db-account-msg').textContent = ACCOUNT.message ||
      (ACCOUNT.signedIn && when && !isNaN(when) ? 'Last synced ' + when.toLocaleString() : '');
    // The panel appearing is height BELOW the columns, so the cap it sits under has to be
    // re-measured. This is the one state change that arrives after a render rather than through
    // one: initAccount awaits the network, and a build with no backend never gets here at all.
    measurePanels();
  }

  async function runSync(silent) {
    if (!accountReady() || ACCOUNT.busy) return null;
    ACCOUNT.busy = true; ACCOUNT.message = ''; renderAccount();
    let res;
    try { res = await window.DeckSync.sync(); }
    catch (e) { res = { ok: false, error: 'unexpected' }; }
    ACCOUNT.busy = false;

    if (res && res.ok) {
      // KEPT-BOTH IS REPORTED, never silent. The player is about to find a deck they did not make
      // sitting in their list, and the one thing worse than a surprise duplicate is an unexplained
      // one. Everything else only speaks when the player asked.
      if (res.keptBoth > 0) {
        ACCOUNT.message = res.keptBoth === 1
          ? 'One deck was edited on two devices, so both versions were kept.'
          : res.keptBoth + ' decks were edited on two devices, so both versions were kept.';
      } else if (!silent) {
        ACCOUNT.message = res.pulled || res.pushed
          ? 'Synced ' + (res.pulled ? res.pulled + ' in' : '') +
            (res.pulled && res.pushed ? ', ' : '') + (res.pushed ? res.pushed + ' out' : '') + '.'
          : 'Everything was already up to date.';
      }
      renderSavedList();
    } else if (res && res.error === 'not_signed_in') {
      ACCOUNT.signedIn = false;
    } else if (!silent) {
      ACCOUNT.message = 'Could not reach your account just now. Your decks are safe on this device.';
    }
    renderAccount();
    return res;
  }

  async function initAccount() {
    if (!accountReady() || window.DeckSync.offline()) return;
    // Reported by the callback when a sign-in did not complete. Read before anything else so the
    // reason survives the first render, and stripped from the URL so a reload does not replay it.
    try {
      const q = new URLSearchParams(window.location.search);
      const err = q.get('signin_error');
      if (err) {
        ACCOUNT.message = err === 'declined'
          ? 'Sign-in was cancelled.'
          : 'Sign-in did not complete. Nothing changed on this device.';
        q.delete('signin_error');
        const rest = q.toString();
        window.history.replaceState(null, '', window.location.pathname + (rest ? '?' + rest : ''));
      }
    } catch (e) { /* a URL we cannot parse is not a reason to skip the panel */ }

    let s;
    try { s = await window.DeckSync.status(); } catch (e) { return; }
    ACCOUNT.configured = !!s.configured;
    ACCOUNT.signedIn = !!s.signedIn;
    renderAccount();
    // One quiet sync on arrival, so a player who built a deck on their phone finds it here without
    // having to know the button exists. Silent: it reports only when it has something to say.
    if (ACCOUNT.signedIn) await runSync(true);
  }

  // ---------------- what this player has bought ----------------
  //
  // ONE READ, AT BOOT, AND NO WAY TO CHANGE IT FROM THIS PAGE. The builder's pool grows by whatever
  // the season sold this player and then holds still for the session — which is right, because the
  // only thing that can change it is a purchase, and purchases happen on library.html. A player who
  // buys a card and comes back here gets it on the next load.
  //
  // NARROWED ONLY WHEN SOMEBODY IS SIGNED IN. Every other answer — no backend, no session, no
  // network, a `file://` copy — leaves OWNED at null, which js/deck-rules.js reads as "not supplied"
  // and answers with the base pool. So the failure direction is the season being ABSENT, never the
  // season being free.
  async function initStore() {
    if (!window.StoreClient || window.StoreClient.offline()) return null;
    let s;
    try { s = await window.StoreClient.state(); } catch (e) { return null; }
    if (!s) return null;
    STORE = s;
    // The balance goes up in the strip whether or not anything was bought — it is the number the
    // player came here wondering about, and js/page-nav.js decides on its own whether there is an
    // account to report on. Done BEFORE the ownership branch below, so a signed-in player who owns
    // nothing still sees what they have to spend.
    if (window.PageNav) window.PageNav.render(s);
    if (!s.signedIn) return s;
    OWNED = new Set(s.owned);
    render();
    return s;
  }

  // The seasonal cards this player could add to THIS deck if they bought them. Not a count of the
  // whole season: a Judo builder is not helped by being told about a Wing Chun card. Style and
  // Family are read the way legalPool reads them, so the number can never name a card the pool
  // would refuse anyway.
  function buyableForStyle() {
    if (!STORE || !STORE.signedIn || !DRAFT.style) return [];
    const C = cardsTable();
    const family = window.FAMILY_OF_STYLE[DRAFT.style];
    return (STORE.catalog || []).map(e => e.cardId).filter(id => {
      const card = C[id];
      if (!card || R.ownsCard(id, OWNED)) return false;
      if (card.ownershipTier === 'Universal') return true;
      if (card.ownershipTier === 'Family') return card.ownershipGroup === family;
      return card.ownershipTier === 'Style' && card.style === DRAFT.style;
    });
  }

  // WHERE A PLAYER LEARNS THE SEASON EXISTS. Reported against the live build: "I don't see how to
  // actually buy cards with credits." A card this player has not bought is ABSENT from the pool by
  // design — that is the rule, and showing a locked row instead would be the lock-with-no-unlock
  // this project refuses. But absent and NON-EXISTENT look identical, and only one of them is true.
  //
  // So the pool says how many cards are missing and where to get them, and says it only to somebody
  // who could act on it: signed in, with cards for THIS Style still unbought. No session, no season,
  // nothing left to buy — no line.
  function renderBuyHint() {
    const box = el('db-buy-hint');
    if (!box) return;
    const n = buyableForStyle().length;
    if (!n) { box.hidden = true; box.innerHTML = ''; return; }
    box.hidden = false;
    box.innerHTML = esc(n === 1
      ? 'One more card for this Style is in the season. '
      : n + ' more cards for this Style are in the season. ') +
      '<a href="library.html">Unlock them in your library</a>' +
      (STORE && typeof STORE.balance === 'number'
        ? esc(' — you have ' + STORE.balance + (STORE.balance === 1 ? ' credit.' : ' credits.'))
        : '');
  }

  function init() {
    buildStaticControls();
    syncFilterControls();
    wire();
    render();
    // AFTER the first render and BEFORE the two account calls. After, because importCode writes
    // into elements render() creates; before, because it is synchronous and neither of those is —
    // a player arriving by link should find the deck open, not watch it replace what they started.
    importFromUrl();
    // Deliberately last, deliberately not awaited: the builder is finished and interactive at this
    // point, and the account panel appears underneath it a moment later or not at all. The owned
    // set arrives the same way — the base pool is on screen first and grows if the season sold this
    // player anything, rather than the page waiting on a request to draw any cards at all.
    initAccount();
    initStore();
  }

  window.DeckBuilder = {
    init: init,
    // Exported so a browser check can assert the number rather than a resize having happened,
    // and so a jsdom suite can prove it REFUSES to publish one when nothing is laid out.
    measurePanels: measurePanels,
    // The card detail sheet, exported so a test can drive the open without synthesising a tap on
    // a tile -- and so the suite can prove the sheet's Add is the SAME addCard the row's + calls,
    // which is the one claim a rendered check cannot make.
    openCardSheet: openCardSheet,
    // The deck sheet and the next-action line, exported on the same principle as everything else
    // here: the claims are about STATE and focus, and a synthesised tap proves the wiring rather
    // than the rule.
    setSheetOpen: setSheetOpen,
    sheetOpen: () => SHEET_OPEN,
    nextActionText: nextActionText,
    setIssuesOpen: setIssuesOpen,
    issuesOpen: issuesOpen,
    // Back to "the player has not said", so the width default can be driven from a test. There is
    // no control that does this -- a player who has answered has answered.
    resetIssuesFold: () => { ISSUES_OPEN = null; IDENTITY_OPEN = null; },
    setIdentityOpen: (o) => { IDENTITY_OPEN = !!o; const f = el('db-identity-wrap'); if (f) f.open = !!o; },
    identityOpen: identityOpen,
    // Exported for tests and for the phases that follow. The builder is driven through these rather
    // than through synthesised DOM events, because what has to be asserted is the DRAFT -- a button
    // that renders disabled and a call that refuses are two different claims and both are checked.
    getDraft: () => ({
      id: DRAFT.id, name: DRAFT.name, style: DRAFT.style, artKey: DRAFT.artKey,
      description: DRAFT.description, cards: DRAFT.cards.slice()
    }),
    setDraft: setDraft,
    addCard: addCard,
    removeCard: removeCard,
    copiesOf: copiesOf,
    canAdd: canAdd,
    issues: issues,
    isLegal: isLegal,
    requestStyle: requestStyle,
    confirmStyleChange: confirmStyleChange,
    cancelStyleChange: cancelStyleChange,
    casualtiesOf: casualtiesOf,
    cloneFrom: cloneFrom,
    newDeck: newDeck,
    openSaved: openSaved,
    attemptSave: attemptSave,
    deleteDraft: deleteDraft,
    // The list's delete, driven the same way as everything else here: the claims are about the
    // STORE and the pending state, and a synthesised click would prove the wiring, not the rule.
    requestDeleteSaved: requestDeleteSaved,
    confirmDeleteSaved: confirmDeleteSaved,
    cancelDeleteSaved: cancelDeleteSaved,
    pendingDelete: () => PENDING_DELETE,
    exportCode: exportCode,
    importCode: importCode,
    // The link half of sharing. shareLink is a pure string function of a code and the current
    // location, so a suite can assert the URL it mints without a clipboard; copyLink is the button.
    shareLink: shareLink,
    copyLink: copyLink,
    // Exported so the ORDER inside it can be driven. It runs once, at init, off the real URL, so
    // there is otherwise no way to ask what happens when the decode below it throws — and that is
    // the only window in which strip-first differs from strip-after at all. See the suite.
    importFromUrl: importFromUrl,
    status: () => STATUS,
    pendingStyle: () => PENDING_STYLE,
    FORMAT: FORMAT,
    // The account panel, exported the same way as everything else here: driven through these in
    // tests rather than through synthesised clicks, so "the panel is hidden" and "the sync refused"
    // stay two separate claims.
    initAccount: initAccount,
    initStore: initStore,
    // What the builder believes this player owns, as a plain array so a test asserts a value rather
    // than a Set's identity. `null` is "not supplied", which is the base pool.
    ownedCards: () => (OWNED ? [...OWNED] : null),
    runSync: runSync,
    signOutAccount: signOutAccount,
    deleteAccount: deleteAccount,
    // The panel's own click dispatch, exported so a test drives the REAL routing rather than a
    // reimplementation of it. A confirm step that exists in the test's model and not in the
    // dispatcher is exactly the bug worth catching.
    accountAction: act => onClick({
      target: { closest: () => ({ getAttribute: n => (n === 'data-act' ? act : null) }) }
    }),
    accountState: () => ({
      configured: ACCOUNT.configured, signedIn: ACCOUNT.signedIn,
      busy: ACCOUNT.busy, message: ACCOUNT.message
    })
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // THE THREE THINGS THAT CHANGE THE BUDGET WITHOUT CHANGING THE DRAFT. A render covers every
  // state this module owns; these are the ones it does not. `load` is the fonts settling (the
  // same trigger page-nav.js measures on, and for the same reason); `resize` is the obvious one
  // and is also where a rewrapped rules blurb shows up; and `toggle` catches the disclosures --
  // saved decks sits ABOVE the columns and the account and share panels BELOW them, so opening
  // any of the three moves one of the two terms. Delegated at the document, in the capture
  // phase, because `toggle` does not bubble.
  window.addEventListener('load', measurePanels);
  window.addEventListener('resize', measurePanels);
  document.addEventListener('toggle', measurePanels, true);

  // js/main.js's back/forward-cache gap, from this page's side. A restore is not a load: init does
  // not re-run, so the saved list shows storage as it stood when the player left -- stale the
  // moment the game page's sync adopted or removed a deck while the builder sat in the cache. Only
  // the LIST re-reads. The draft is in-memory work the restore rightly preserves, and every action
  // on a stale id already answers for itself ("That deck is no longer saved", "That deck is
  // already gone").
  window.addEventListener('pageshow', (e) => { if (e.persisted) renderSavedList(); });
})();
